import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Wifi, Monitor, Network, Video, Wrench,
  ArrowRight, CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const services = [
  { text: 'Conference Wi-Fi setup', icon: Wifi },
  { text: 'Digital displays & screens', icon: Monitor },
  { text: 'Event networking infrastructure', icon: Network },
  { text: 'Livestream systems', icon: Video },
  { text: 'Technical troubleshooting', icon: Wrench },
];

const ServicesEventsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const eventsServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Fearless IT Event Services",
    "serviceType": "Convention Technology and Event Infrastructure",
    "description": "Conference Wi-Fi networks, digital displays, livestream systems, check-in technology, and event troubleshooting for Las Vegas conventions and events.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Fearless IT",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Las Vegas",
        "addressRegion": "NV",
        "addressCountry": "US"
      },
      "telephone": "(702) 727-1773",
      "url": "https://fearlessit.io"
    },
    "areaServed": {
      "@type": "City",
      "name": "Las Vegas"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Event Technology Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Conference Wi-Fi Networks" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Digital Displays & Screens" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Livestream Systems" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Check-in & Registration Tech" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Event Troubleshooting & Support" }}
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="services-events-page">
      <SEO 
        title="Event Services - Convention Technology & Event Infrastructure"
        description="Fearless IT Events: Las Vegas hosts some of the world's largest conferences and events. We provide conference Wi-Fi networks, digital displays, livestream systems, check-in technology, and event troubleshooting support."
        keywords="convention technology Las Vegas, event Wi-Fi Las Vegas, conference technology Nevada, digital displays events, livestream systems Las Vegas, event IT support, trade show technology, convention center IT"
        url="/services/events"
        image="https://static.prod-images.emergentagent.com/jobs/1c71d773-84d6-4251-9bae-60816a9265db/images/afe88943394a75e16555d2e5531649295b0df642c7a7ff628793f11d6275356e.png"
        serviceName="Convention Technology and Event Infrastructure"
        structuredData={eventsServiceSchema}
        isLocalPage={true}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-violet-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full mb-6">
            <Wifi className="h-4 w-4 text-violet-600" />
            <span className="text-sm font-medium text-violet-700">Event Services</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-black mb-4" data-testid="events-title">
            Fearless IT Events
          </h1>
          <p className="text-xl sm:text-2xl font-semibold mb-6" style={{ background: 'linear-gradient(to right, #7C3AED, #A855F7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Convention Technology & Event Infrastructure
          </p>
          <p className="text-lg sm:text-xl text-slate-gray max-w-2xl mx-auto leading-relaxed">
            Las Vegas hosts some of the world's largest conferences and events. We provide the technology that keeps them running smoothly.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-sm font-semibold text-slate-gray uppercase tracking-wider mb-8 text-center">
            Services Include
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-12">
            {services.map((service, idx) => {
              const IconComponent = service.icon;
              return (
                <div 
                  key={idx} 
                  className="flex items-center gap-4 p-5 bg-gray-50 rounded-xl border border-gray-100 hover:border-violet-200 hover:bg-violet-50/30 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-lg font-medium text-carbon-black">{service.text}</span>
                </div>
              );
            })}
          </div>
          
          {/* Closing Text */}
          <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-2xl p-8 text-center border border-violet-100">
            <CheckCircle2 className="h-10 w-10 text-violet-600 mx-auto mb-4" />
            <p className="text-lg sm:text-xl text-carbon-black font-medium">
              Ideal for conference organizers, exhibitors, and event production teams.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Planning an event in Las Vegas?
          </h2>
          <p className="text-slate-gray mb-8 max-w-xl mx-auto">
            Let's make sure your technology works flawlessly from setup to teardown.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/consultation">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-slate-gray text-slate-gray hover:border-violet-500 hover:text-violet-500 transition-all">
                Book Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default ServicesEventsPage;
